import { Component, Input, OnInit } from "@angular/core"
import { ActivatedRoute } from "@angular/router"
import { Subscription } from "rxjs"
import { FacetingService } from "../../../services/faceting.service"
import { SearchingService } from "../../../services/searching.service"
import { facetType } from "../../../utils/types"

@Component({
    selector: "app-generic-facet",
    templateUrl: "./generic-facet.component.html",
    styleUrls: ["./generic-facet.component.less"],
})
export class GenericFacetComponent implements OnInit {
    @Input() public dynamicFacetName: string = ""
    @Input() public facetName: string = ""
    @Input() public layout: string = ""
    @Input() public isCollapsed: boolean = true
    @Input() public syncFacets: boolean = false
    @Input() public keyOrder: String[] = null
    @Input() public defaultOptions: String[] = null
    @Input() public staticOptions: boolean = false
    @Input() public allowedFacetKeys: String[] = null // null for all
    public subscriptions: Subscription[] = []
    public options: any[] = []
    // For padding if the options number does not divide correctly
    public fakeOptions: any[] = []
    public title: string = ""
    public selectedFiltersCount: number = 0

    constructor(
        public facetingService: FacetingService,
        private searchingService: SearchingService,
        private route: ActivatedRoute
    ) {}

    public ngOnInit() {
        if (this.dynamicFacetName) {
            // If this is a dynamic facet, it will get both its display title and values
            // whenever they change from the faceting service
            this.facetingService[`${this.dynamicFacetName}_title`].subscribe((title) => {
                this.title = title
            })
            this.facetingService[`${this.dynamicFacetName}`].subscribe((facet) => {
                this.setOptions(facet)
            })
        } else {
            // If this isn't a dynamic facet, it will get its title only once
            // from the faceting service.
            this.title = this.facetingService[`${this.facetName}_title`]

            if (!this.staticOptions) {
                // there are currently 2 availableFacetsChanged subscribers, here and in the facetService
                // the one from the facetService is trusted that will always trigger first so that the options can be populated
                this.setOptions(this.facetingService[`${this.facetName}`])
                this.subscriptions.push(
                    this.searchingService.availableFacetsChanged.subscribe((data) => {
                        this.setOptions(this.facetingService[`${this.facetName}`])
                    })
                )
            } else {
                this.options = this.defaultOptions.map((label) => ({
                    label,
                    checked: false,
                }))
            }
        }
        this.subscriptions.push(
            this.facetingService.filtersChanged.subscribe((selectedFilters) => {
                this.selectOptions(selectedFilters)
                this.selectedFiltersCount = selectedFilters.filter(
                    (x) => x.split(":")[0] === this.facetName
                ).length
            })
        )

        if (this.syncFacets) {
            this.isCollapsed = true
            this.subscriptions.push(
                this.facetingService.toggleFilter.subscribe((facetType: facetType) => {
                    if (facetType.type !== this.facetName) {
                        this.isCollapsed = true
                    }
                })
            )
        }

        this.updateGridPaddingOptions()
    }

    public selectOptions(selectedFilters: string[]) {
        this.options.map((option) => {
            option.checked = selectedFilters.indexOf(this.getFilterName(option)) !== -1
        })
    }

    public setOptions(options: any[]) {
        let newOptions = options.slice()
        if (this.allowedFacetKeys) {
            newOptions = newOptions.filter(
                (facet) => this.allowedFacetKeys.indexOf(facet.label) !== -1
            )
        }
        if (this.keyOrder) {
            newOptions.sort((a, b) => {
                return this.keyOrder.indexOf(a.label) - this.keyOrder.indexOf(b.label)
            })
        }
        this.options = newOptions
        this.updateGridPaddingOptions()
        this.selectOptions(this.facetingService.appliedFilters)
    }

    public updateGridPaddingOptions() {
        if (this.layout !== "grid") {
            this.fakeOptions = []
            return
        }
        const lastRowCount = this.options.length % 3
        if (lastRowCount === 0) {
            this.fakeOptions = []
        } else if (lastRowCount === 1) {
            this.fakeOptions = ["", ""]
        } else if (lastRowCount === 2) {
            this.fakeOptions = [""]
        }
    }

    private getFilterName(option) {
        const facetName = this.dynamicFacetName || this.facetName
        if (option.id) {
            return `${facetName}:${option.id}`
        }
        return `${facetName}:${option.label.replace(/\s/g, " ")}`
    }

    public onCheckboxChange(event, option) {
        option.checked = !option.checked

        if (event.target.checked) {
            this.facetingService.addFilter(this.getFilterName(option), this.route)
        } else {
            this.facetingService.removeFilter(this.getFilterName(option), this.route)
        }
    }

    public onToggle() {
        this.isCollapsed = !this.isCollapsed
        if (this.syncFacets) {
            this.facetingService.broadcastOnToggle(this.isCollapsed, this.facetName)
        }
    }

    public ngOnDestroy() {
        this.subscriptions.map((subscription) => subscription.unsubscribe())
    }
}
